<template>
    <b-modal
        :visible="value"
        :title="title"
        size="xl"
        hide-footer
        @hidden="hide"
    >
        <b-table
            small
            responsive
            outlined
            show-empty
            head-variant="light"
            :busy="loading"
            :items="pagedRequests"
            :fields="fields"
            :empty-text="emptyText"
        >
            <template #table-busy>
                <div class="text-center my-3">
                    <b-spinner small class="mr-2" /> Loading reports...
                </div>
            </template>
            <template #cell(report_type)="data">{{ reportLabel(data.item.report_type) }}</template>
            <template #cell(requested_by)="data">{{ requestedByLabel(data.item) }}</template>
            <template #cell(status)="data">
                <b-badge :variant="statusVariant(data.item.status)">{{ data.item.status }}</b-badge>
            </template>
            <template #cell(comment)="data">
                <span v-if="data.item.comment" v-b-tooltip.hover :title="data.item.comment">
                    {{ commentPreview(data.item.comment) }}
                </span>
                <span v-else class="text-muted">-</span>
            </template>
            <template #cell(created_at)="data">{{ formatDate(data.item.created_at) }}</template>
            <template #cell(completed_at)="data">
                {{ data.item.completed_at ? formatDate(data.item.completed_at) : '-' }}
            </template>
            <template #cell(download)="data">
                <b-button
                    v-if="data.item.status === 'complete'"
                    size="sm"
                    variant="outline-primary"
                    :disabled="downloading_ids.includes(data.item.id)"
                    @click="downloadReport(data.item)"
                >
                    Download
                </b-button>
                <span v-else-if="data.item.status === 'failed'" class="text-danger">Failed</span>
                <span v-else class="text-muted">Pending</span>
            </template>
        </b-table>

        <div v-if="requests.length" class="d-flex align-items-center justify-content-between mt-3 mb-4">
            <div class="d-flex align-items-center">
                <span class="mr-2">Rows per page</span>
                <b-form-select v-model="per_page" :options="[10, 25, 50]" size="sm" style="width: 90px;" />
            </div>
            <b-pagination
                v-model="current_page"
                :per-page="per_page"
                :total-rows="requests.length"
                align="right"
                size="sm"
                class="mb-0"
            />
        </div>
    </b-modal>
</template>

<script>
import {downloadBlob} from '@/common/fileDownload'

const reportLabels = {
    untitled_panels: 'Untitled Panels',
    untitled_weeks: 'Untitled Weeks',
    article_by_sku: 'Article By SKU',
    printables_by_sku: 'Printables By SKU',
    related_media_videos_by_sku: 'All Publication Related Media',
    weekly_phenomenon_by_unit: 'Weekly Phenomenon By Unit',
    articles_with_narration: 'Articles With Narration',
    articles_without_narration: 'Articles Without Narration',
}

export default {
    name: 'EntityReportRequestsModal',
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        publication: {
            type: Object,
            required: true,
        },
        entityType: {
            type: String,
            required: true,
            validator: value => ['publication', 'unit', 'week'].includes(value),
        },
        entityId: {
            type: Number,
            default: null,
        },
        entityLabel: {
            type: String,
            default: '',
        },
    },
    data: () => ({
        requests: [],
        loading: false,
        current_page: 1,
        per_page: 10,
        poller: null,
        downloading_ids: [],
        fields: [
            {key: 'id', label: 'Request ID'},
            {key: 'report_type', label: 'Report'},
            {key: 'requested_by', label: 'Requested By'},
            {key: 'status', label: 'Status'},
            {key: 'comment', label: 'Comment'},
            {key: 'created_at', label: 'Requested'},
            {key: 'completed_at', label: 'Completed'},
            {key: 'download', label: ''},
        ],
    }),
    computed: {
        title() {
            return this.entityLabel ? `Reports: ${this.entityLabel}` : 'Reports'
        },
        emptyText() {
            return `No reports have been requested for this ${this.entityType}.`
        },
        requestParams() {
            const params = {
                scope: 'all',
                publication_id: this.publication.id,
            }

            if (this.entityType === 'unit') {
                params.unit_group_id = this.entityId
            } else if (this.entityType === 'week') {
                params.week_id = this.entityId
            }

            return params
        },
        pagedRequests() {
            const start = (this.current_page - 1) * this.per_page
            return this.requests.slice(start, start + this.per_page)
        },
    },
    watch: {
        value(isVisible) {
            if (isVisible) {
                this.open()
            } else {
                this.stopPolling()
            }
        },
        per_page() {
            this.current_page = 1
        },
    },
    beforeDestroy() {
        this.stopPolling()
    },
    methods: {
        async open() {
            this.current_page = 1
            await this.getReportRequests()
            this.stopPolling()
            this.poller = window.setInterval(this.getReportRequests, 25000)
        },
        hide() {
            this.stopPolling()
            this.$emit('hide')
        },
        stopPolling() {
            if (this.poller) {
                window.clearInterval(this.poller)
                this.poller = null
            }
        },
        async getReportRequests() {
            if (!this.publication.id || !this.entityId) {
                return
            }

            this.loading = true
            try {
                const response = await this.axios.get('/api/reports/requests', {
                    params: this.requestParams,
                })
                this.requests = response.data
            } finally {
                this.loading = false
            }
        },
        reportLabel(reportType) {
            return reportLabels[reportType] || reportType
        },
        requestedByLabel(reportRequest) {
            const requester = reportRequest.requested_by
            if (!requester) {
                return 'Unknown'
            }

            return `${requester.first_name || ''} ${requester.last_name || ''}`.trim()
                || requester.email
                || 'Unknown'
        },
        statusVariant(status) {
            return {
                complete: 'success',
                failed: 'danger',
                processing: 'warning',
            }[status] || 'secondary'
        },
        commentPreview(comment) {
            return comment.length > 50 ? `${comment.slice(0, 50)}...` : comment
        },
        formatDate(value) {
            return this.moment(value).format('MM-DD-YYYY h:mm A')
        },
        async downloadReport(reportRequest) {
            if (this.downloading_ids.includes(reportRequest.id)) {
                return
            }

            this.downloading_ids.push(reportRequest.id)
            try {
                const response = await this.axios.get(
                    `/api/reports/download/${reportRequest.id}`,
                    {responseType: 'blob'}
                )
                downloadBlob(response.data, reportRequest.file_name || `report-${reportRequest.id}.csv`)
            } finally {
                this.downloading_ids = this.downloading_ids.filter(id => id !== reportRequest.id)
            }
        },
    },
}
</script>
